/**
 * @file: Cube类
 * @author: jun.li@dmall.com
 */

import Utils from '../../utils/util';
import UUID from '../../utils/uuid';
import Event from './events';

interface CubeStatus {
    create: number;
    inited: number;
    rendered: number;
    destroyed: number;
};

const CUBE_STATUS_MAP: CubeStatus = {
    create: 1,
    inited: 8,
    rendered: 16,
    destroyed: 32,
};

let whiteList: string[] = ['init', 'destroy', 'update', 'render'];
let blackList: string[] = ['classInit', 'tpl'];
let BEHAVIOR_LIST: any[] = [];
let DEFAULT_DATAKEYS: string[] = ['sourceConfig'];
let cubeObjBlackKeyList: string[] = ['_sourceConfig'];

class Cube {
    jView: any = null;
    jContainer: any = null;
    id: string | null = null;
    cubeObj: any = null;
    cubes: any[] = [];
    parentId: string | null = null;
    events: any = {};
    status: number = CUBE_STATUS_MAP.create;
    jParent: any = null;
    isWalk: boolean = false;
    isWalkAsync: boolean = false;
    dslot: string = '';
    dataKeys: any[] = [];
    _init: () => any = () => {};
    _destroy: () => any = () => {};
    _update: (params?) => any = () => {};
    _render: () => any = () => {};

    static BEHAVIOR_LIST: FreeObjMap = {};

    static registCube(params): FreeObjMap {
        let { behavior,  cubeName} = params;

        // 类型判断
        if (!!Cube.BEHAVIOR_LIST[cubeName]) {
            !params.callByTest && console.error('Cube: ' + cubeName + '已被注册');
            return {
                code: 1,
                msg: '已被注册'
            };
        }
        Cube.BEHAVIOR_LIST[cubeName] = behavior;
        return {
            code: 0,
            msg: '注册成功'
        };
    }

    static unRegistCube(): void {
        Cube.BEHAVIOR_LIST = {};
    }

    constructor(cubeObj: any, parentCube?: any, hooks?: FreeObjMap) {
        this.classInit(cubeObj, parentCube, hooks);
    }

    classInit(cubeObj, parentCube, hooks?: FreeObjMap): void {
        // 创建标准cube模型
        createStanderdCubeObj(this, cubeObj, parentCube, Cube.BEHAVIOR_LIST, hooks);

        // 这里是否需要fire事件，看需要
        this.status = CUBE_STATUS_MAP.create;
    }

    add(cube: any, index: number, callback: () => any): void {
        let self: any = this;

        if (self.status <= -1) {
            // 自己还没初始化就不能挂载
            return;
        }

        if (!(cube instanceof Cube)) {
            cube = new Cube(cube, self);
        }

        cube.appendTo(self, index, callback);
    }

    appendTo(...args): void {
        let self: any = this;
        let parentCube: any;
        let index: number = -1;
        let callback: any;

        // parentCube可能是cube或者jq对象
        parentCube = arguments[0];
        if (arguments.length == 3) {
            index = arguments[1];
            callback = arguments[2];
        } else if (arguments.length == 2) {
            (typeof arguments[1] == 'number') && (index = arguments[1]);
            (typeof arguments[1] == 'function') && (callback = arguments[1]);
        }

        if (parentCube.fire && parentCube.fire('beforeAddCube', parentCube, parentCube) === false) {
            callback && callback(self);
            return;
        }

        // 先挂载数据
        addCube(self, parentCube, index);

        // 继承继承父节点的事件，但移除时不删除？
        if (parentCube && parentCube.events && parentCube.events.inheritEvents) {
            self.walk(function(cube) {
                // 直接替换算了，不然会导致拖拽几次事件变得越来越多
                self.events.inheritEvents.callbacks = Utils.deepCopy(parentCube.events.inheritEvents.callbacks);
            });
        }
        initCube(self, parentCube, Cube.BEHAVIOR_LIST, index);
        callback && callback(self);
        if (parentCube && parentCube.fire) {
            parentCube.fire('afterAddCube', self, parentCube);
        }
        else {
            self.fire('afterAddCube', self, parentCube);
        }
    }

    walk(callback: any): any {
        let self = this;
        let result;

        result = walk(this, callback);

        return result;
    }

    remove(cube): void {
        var index = -1;
        var i, c,
            self = this,
            cubes = self.cubes;

        if (!cubes || !cubes.length) {
            return;
        }

        if (cube instanceof Cube) {
            for (i = 0; c = cubes[i]; ++i) {
                if (c == cube) {
                    index = i;
                    break;
                }
            }
        }

        if (index >= 0) {
            self.cubes.splice(index, 1);
        }
    }

    walkAsync(callback): void {
        if (this.isWalk) return;
        this.isWalkAsync = true;
        walkAsync(null, this, 0, callback);
    }

    on(): void {
        var isInherit, eventName, callback, context;
        if (arguments[0] === true) {
            isInherit = true;
        }
        eventName = isInherit ? arguments[1] : arguments[0];
        callback = isInherit ? arguments[2] : arguments[1];
        context = isInherit ? arguments[3] : arguments[2];

        this.events.privateEvents.on(eventName, callback);
    }

    onEach(eventName, callback, context): void {
        var self = this;

        // 状态如果已经过滤，可直接触发
        if (self.status < CUBE_STATUS_MAP.rendered && self.status >= CUBE_STATUS_MAP.inited) {
            self.events.inheritEvents.on(eventName, callback);
            return;
        }

        // 如果完成节点构造工作
        self.walk(function(node) {
            node.events.inheritEvents.on(eventName, callback);
        })
    }

    fire(eventName, param): any {
        var events = this.events;
        if (events.privateEvents.fire(eventName, param) === false || events.inheritEvents.fire(eventName, param) === false) {
            return false;
        }
    }

    getDropArea(): any {
        var that = this;
        var jView = that.jView,
            slot = that.dslot
                ? jView.find('.' + that.dslot)
                : jView;

        return slot;
    }

    init() {
        this._init && this._init();
    }

    destroy() {
        this._destroy && this._destroy();
    }

    update(newValue) {
        this._update && this._update(newValue);
    }

    refresh() {
        this._render && this._render();
    }

    render(insertIndex: number) {
        var that = this;
        //这里的jContainer slot jquery 对象
        if (that.jView) {
            that.jView.attr('data-instanceid', that.id);
        }
        if (that.jView && that.jContainer) {
            let children = that.jContainer.children();

            if (insertIndex === -1 || insertIndex >= children.length) {
                that.jView.appendTo(that.jContainer);
            }
            else if (insertIndex === 0) {
                if (children.length) {
                    children.eq(0).before(that.jView);
                }
                else {
                    that.jView.appendTo(that.jContainer);
                }
            }
            else {
                children.eq(insertIndex - 1).after(that.jView);
            }
        }
        that._render && that._render();
    }
};

// initCube时调用
function addCube(cube: any, parentCube: any, index: number): void {
    parentCube.cubes = parentCube.cubes || [];
    index = (index >= parentCube.cubes.length || index == -1) ? parentCube.cubes.length : index;
    parentCube.cubes.splice(index, 0, cube);
};

function walk(cube: any, callback: any): any[] {
    var r = [];
    if (!cube) {
        return r;
    }
    var i, c;

    if (!cube.cubes || !cube.cubes.length) {
        cube.cubes = [];
    }
    var temp2 = [];
    for (i = 0; c = cube.cubes[i]; ++i) {
        temp2 = walk(c, callback);
        if (temp2.length > 0) {
            r = r.concat(temp2);
        }
    }

    var temp = callback(cube);
    if (temp) {
        r = r.concat([cube]);
    }
    return r;
}

function initCube(cube: any, parentCube: any, behaviorList, index: number): void {
    let newCubes: any[] = [];

    initBehavior(cube, behaviorList);
    initDom(cube, parentCube);

    if (cube.cubes && cube.cubes.length) {
        for (let i: number = 0; i < cube.cubes.length; ++i) {
            initCube(cube.cubes[i], cube, behaviorList, -1);
            newCubes.push(cube.cubes[i]); // 这里处理add方法
        }
        cube.cubes = newCubes;
    }

    if (cube.status < CUBE_STATUS_MAP.inited) {
        cube.init();
        // 这里还需要初始化Dom
        cube.fire('inited', cube);
    }
    if (cube.status < CUBE_STATUS_MAP.rendered) {
        cube.render && cube.render(index) && cube.fire('rendered', cube); // render后续会执行多次
    }
    cube.status = CUBE_STATUS_MAP.rendered;
};

function initBehavior(cube, behaviorList): void {
    var behavior = behaviorList[cube.cubeName];
    var k;

    if (!behavior) {
        console.error(cube.cubeName + ':未找到对应注册模块');
        return;
    }
    for (k in behavior) {
        // 黑名单完全不动
        if (blackList.indexOf(k) >= 0) {
            continue;
        }

        // 白名单打下划线，外层做代理
        if (whiteList.indexOf(k) >= 0) {
            cube['_' + k] = behavior[k];
            continue;
        }

        // 其他的直接覆盖
        cube[k] = behavior[k];
    }
}

function initDom(cube, parentCube?): void {
    var selector = '';

    if (cube.jView ) {
        cube.jView = cube.jView;
    }
    else {
        cube.jView = Cube.BEHAVIOR_LIST[cube.cubeName].tpl ? Cube.BEHAVIOR_LIST[cube.cubeName].tpl.clone(true) : $(document.createElement('div'));
    }

    cube.jContainer = cube.jContainer || getSlot(parentCube, cube);
};

function getSlot(parentCube, cube) {
    var jView = isJquery(parentCube) ? parentCube : parentCube.jView;
    var slot = cube.container
            ? jView.find(cube.container)
            : jView.find('.' + parentCube.dslot);

    slot = slot.length === 0 ? jView : slot;

    return slot;
}

function isJquery(obj): boolean {
    return !(obj instanceof Cube) && (obj.length == 1 && typeof obj == 'object');
}

function walkAsync(parentList, cube, index, callback): void {
    parentList = parentList || [];
    var cubes = cube.cubes,
        parentCube,
        parentObj,
        currentChilrenCube;


    // 当前子节点已经编列完，或无子节点
    if (index >= cubes.length || !cubes || cube.length || !cubes[index]) {
        // 这里避免=和>的情况分别执行2次
        index == cubes.length && callback(cube, parentList);
        parentObj = parentList.pop();
        parentObj
            ? walkAsync(parentList, parentObj.cube, parentObj.index + 1, callback)
            : cube.isWalkAsync = false;
        return;
    }

    // 存在当前节点情况
    currentChilrenCube = cubes[index];
    // 如果没有压入则压入当前子节点
    if (!parentList.length || parentList[parentList.length - 1].cube != cube) {
        parentList.push({
            cube: cube,
            index: index // 记录当前延展到哪个兄弟节点
        });
    }

    // 有子节点，继续向下延展
    if (currentChilrenCube.cubes && currentChilrenCube.cubes.length) {
        setTimeout(function() {
            walkAsync(parentList, currentChilrenCube, 0, callback);
        }, 1);
        return;
    }

    // 没有子节点，执行自己，并延展看看兄弟节点当前下一个兄弟节点
    callback(currentChilrenCube, parentList);
    parentObj = parentList[parentList.length - 1];
    parentObj.index += 1;
    setTimeout(function() {
        walkAsync(parentList, cube, parentObj.index, callback);
    }, 1);
}

function initChildren(cube, parentCube, hooks?: FreeObjMap): any {
    var i, c, result, newCube;

    result = {
        cubes: []
    };

    // 这里还要包含初始化资源的流程，尽量在一个遍历里搞定
    if (!cube.cubes || !cube.cubes.length) {
        return result; // 这里应该把资源也返回
    }

    for (i = 0; c = cube.cubes[i]; ++i) {
        newCube = new Cube(c, cube, hooks);
        result.cubes.push(newCube);
    }

    return result;
}

function initEvents(caller, parentCube): void {
    var events;

    events = caller.events = Utils.deepCopy(caller.events);
    events.privateEvents = new Event();
    events.inheritEvents = new Event();

    // 继承父级的方法
    if (parentCube && parentCube.events && parentCube.events.inheritEvents) {
        Utils.mix(events.inheritEvents.callbacks, parentCube.events.inheritEvents.callbacks);
    }
}

function initData(caller, parentCube, cubeObj): void {
    var k;

    caller.dataKeys = Utils.deepCopy(DEFAULT_DATAKEYS);

    // 这里标志出原生的key
    for (k in cubeObj) {
        if (cubeObjBlackKeyList.indexOf(k) >= 0 || caller.dataKeys.indexOf(k) >= 0) {
            continue;
        }
        caller.dataKeys.push(k);
        caller[k] = cubeObj[k];
    }
    caller.id = caller.id || new UUID().id;
    caller.parentId = parentCube && parentCube instanceof Cube && parentCube.id;
}

function createStanderdCubeObj(cube, cubeObj, parentCube, behaviorList, hooks?: FreeObjMap): any {
    // TODO：1.判断节点是否是标准节点，如果是则直接返回
    if (cube.status && cube.status >= CUBE_STATUS_MAP) {
        return cube;
    }
    var self = cube,
        childrenInfo;

    initEvents(self, parentCube);
    initData(self, parentCube, cubeObj);
    childrenInfo = initChildren(self, parentCube, hooks);
    hooks && hooks.created && hooks.created(self);
    self.cubes = childrenInfo.cubes;
}

export { Cube };
